import { api } from '../../lib/api';
import type { CallLogRow } from '../../types/leadCall';
import type { LeadHistoryPage } from '../../lib/speedToLead/leadHistory';
import { funnelQueryString, leadQueryString } from '../../lib/leads/leadQuery';
import { splitQueryString } from '../../lib/leads/leadSplit';
import type { LeadCallOptions } from '../../types/leadSplit';
import type { LeadSplitDetail, LeadSplitFilters, LeadSplitPage, StaffLeadClub } from '../../types/leadSplit';
import type { LeadFilters, LeadFunnel, LeadListPage } from '../../types/leadList';
import type { LeadOutcome } from '../../types/leadOutcome.js';
import type {
  NumberPool,
  SpeedToLeadSettings,
  SpeedToLeadStaffEntry,
} from '../../types/speedToLead';

// Kyck-side Speed to Lead API (Settings -> Speed to Lead). ⚠️ /api/speed-to-lead is the STAFF surface;
// the club's own page uses clientPortal/portal/speedToLead/portalApi.ts and never imports this file
// (a source guard pins it).

const BASE = '/api/speed-to-lead';

export interface ClubSettingsResponse {
  settings: SpeedToLeadSettings;
  isDefault: boolean;
  team: Array<{ id: string; name: string; role: string; hasMobile: boolean }>;
  warnings: Array<{ teamMemberId: string; message: string }>;
  accountName: string;
  /** False until the call engine ships (Stage 3): the Speed to Lead toggle reads "Coming soon". */
  callsAvailable: boolean;
  bots: Array<{ id: string; name: string; isActive: boolean; mode: string; numberWarning: string | null }>;
  pool: { id: string; name: string } | null;
}

export type SettingsBody = Omit<SpeedToLeadSettings, 'accountId' | 'updatedAt' | 'updatedBy' | 'updatedByName' | 'calls'> & {
  /** Optional: a write that omits it keeps the stored block. */
  calls?: SpeedToLeadSettings['calls'];
};

export const fetchClubSettings = (accountId: string) =>
  api<ClubSettingsResponse>(`${BASE}/clubs/${encodeURIComponent(accountId)}/settings`);

export const saveClubSettings = (accountId: string, body: SettingsBody) =>
  api<{ settings: SpeedToLeadSettings }>(`${BASE}/clubs/${encodeURIComponent(accountId)}/settings`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const saveClubStaffList = (accountId: string, staffList: SpeedToLeadStaffEntry[]) =>
  api<{ settings: SpeedToLeadSettings }>(`${BASE}/clubs/${encodeURIComponent(accountId)}/staff-list`, {
    method: 'PUT',
    body: JSON.stringify({ staffList }),
  });

export interface DirectTestLeadBody {
  name?: string;
  mobile: string;
  source: 'meta_lead_form' | 'website' | 'phone';
  startFresh: boolean;
}

/** Settings -> Speed to Lead -> "Send test lead" (admin). Goes through intake only: no relay, no CRM delivery. */
export const sendDirectTestLead = (accountId: string, body: DirectTestLeadBody) =>
  api<{ leadSubmissionId: string; intake: { kind: string; detail: string } | null; closedConversations: number }>(
    `${BASE}/clubs/${encodeURIComponent(accountId)}/test-lead`,
    { method: 'POST', body: JSON.stringify(body) },
  );

export const fetchClubCalls =(accountId: string) => api<{ calls: CallLogRow[] }>(`${BASE}/clubs/${encodeURIComponent(accountId)}/calls`);

/** A signed playback URL of at most five minutes. ⚠️ Never log it or keep it. */
export const fetchRecordingUrl = (kind: CallLogRow['kind'], id: string) =>
  api<{ url: string; expiresAt: string }>(`${BASE}/calls/${kind}/${encodeURIComponent(id)}/recording-url`);

export const confirmCallOutcome = (kind: CallLogRow['kind'], id: string, outcome: string) =>
  api<{ outcome: unknown }>(`${BASE}/calls/${kind}/${encodeURIComponent(id)}/outcome`, { method: 'POST', body: JSON.stringify({ outcome }) });

export const fetchLeadHistory = (handle: { call: string } | { conversation: string }, cursor: string | null) => {
  const q = new URLSearchParams('call' in handle ? { call: handle.call } : { conversation: handle.conversation });
  if (cursor) q.set('cursor', cursor);
  return api<LeadHistoryPage>(`${BASE}/lead-history?${q.toString()}`);
};

export const deleteRecording = (kind: CallLogRow['kind'], id: string) =>
  api<{ deleted: boolean }>(`${BASE}/calls/${kind}/${encodeURIComponent(id)}/recording`, { method: 'DELETE' });

export interface PoolsResponse {
  pools: NumberPool[];
  numbers: Array<{ e164: string; provider: string; purpose: string; active: boolean; warning: string | null }>;
}

export type PoolBody = Pick<NumberPool, 'name' | 'brandAccountId' | 'clubAccountIds' | 'numbers' | 'groupStaff' | 'primaryNumber'>;

export const fetchPools = () => api<PoolsResponse>(`${BASE}/pools`);
export const createPool = (body: PoolBody) => api<{ pool: NumberPool }>(`${BASE}/pools`, { method: 'POST', body: JSON.stringify(body) });
export const updatePool = (id: string, body: PoolBody) =>
  api<{ pool: NumberPool }>(`${BASE}/pools/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) });
export const deletePool = (id: string) => api<{ deleted: boolean }>(`${BASE}/pools/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ---- Stage 4.5C: the lead list, funnel and lead-level actions. `accountId` null = every club (admin only on the server).
export const fetchLeads = (accountId: string | null, filters: LeadFilters, cursor: string | null) =>
  api<LeadListPage>(`${BASE}/leads${leadQueryString(filters, { accountId, cursor })}`);
export const fetchLeadFunnel = (accountId: string | null, range: { receivedFrom?: string; receivedTo?: string }) =>
  api<LeadFunnel>(`${BASE}/leads/funnel${funnelQueryString(range, accountId)}`);
export const setLeadOutcome = (leadId: string, outcome: LeadOutcome) =>
  api<{ outcome: string }>(`${BASE}/leads/${encodeURIComponent(leadId)}/outcome`, { method: 'POST', body: JSON.stringify({ outcome }) });
export const setLeadNeedsPerson = (leadId: string, on: boolean) =>
  api<{ needsPerson: boolean }>(`${BASE}/leads/${encodeURIComponent(leadId)}/needs-person`, { method: 'POST', body: JSON.stringify({ on }) });

// ---- Leads centre v2, Part B: call a lead through our system. The lead is addressed by its hashed id; no number travels.
export const fetchLeadCallOptions = (leadId: string) => api<LeadCallOptions>(`${BASE}/leads/${encodeURIComponent(leadId)}/call-options`);
export const startLeadCall = (leadId: string, teamMemberId: string) =>
  api<{ started: true }>(`${BASE}/leads/${encodeURIComponent(leadId)}/call`, { method: 'POST', body: JSON.stringify({ teamMemberId }) });
// ---- Part C: text a lead, and the Sam switch. The lead is addressed by its hashed id; the text is the only thing in the body.
export const sendLeadText = (leadId: string, text: string) =>
  api<{ ok: true; messageId: string }>(`${BASE}/leads/${encodeURIComponent(leadId)}/text`, { method: 'POST', body: JSON.stringify({ text }) });
export const setLeadSamPaused = (leadId: string, paused: boolean) =>
  api<{ paused: boolean }>(`${BASE}/leads/${encodeURIComponent(leadId)}/sam`, { method: 'POST', body: JSON.stringify({ paused }) });

// ---- Phase 2, Part B: the shared Leads split view. `accountId` null = every club (admin only on the server).
export const fetchLeadSplit = (accountId: string | null, filters: LeadSplitFilters, cursor: string | null) =>
  api<LeadSplitPage>(`${BASE}/lead-split${splitQueryString(filters, { accountId, cursor })}`);
export const fetchLeadClubs = () => api<{ clubs: StaffLeadClub[] }>(`${BASE}/lead-clubs`).then((r) => r.clubs);
export const fetchLeadSplitDetail = (leadId: string, cursor?: string | null) =>
  api<LeadSplitDetail>(`${BASE}/lead-split/${encodeURIComponent(leadId)}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
